import type { ManualTransferJob, ManualTransferOptions } from '@ctcells/api'
import type { Batch, ModuleSnapshot, PreflightResult } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import {
  QUICK_IDLE_POLL_MS,
  QUICK_IDLE_TIMEOUT_MS,
  QUICK_PLACE_TIMEOUT_MS,
  QUICK_RELEASE_SETTLE_MS,
} from '../screens/batches/devQuickReset.ts'

import {
  RecreateVersionMismatch,
  recipeVersionRefusal,
  recreateAndCheck,
  recreateSourceOf,
  type RecreatePorts,
  type RecreateSource,
} from './recreateBatch.ts'

/**
 * 시작 창의 **[작업 중단 후 내려놓기]** — 6축이 트레이를 쥔 채(`HANDOFF`) 멈춰 있고, 그 트레이를 내려놓을 길(`place_held`)을
 * **지금 시작하려는 작업 자신이** 막고 있을 때의 출구(히나 실측 2026-10-23 ②⑤ · 2026-10-24 재현 2fb05857).
 *
 * 서버 실물(`services/manual_transfer.facts.common`): 종착 안 한 배치가 하나라도 있으면 수동 이송이 전부 꺼진다 —
 * `created` 작업도 센다(「시작되면 같은 자리를 쓰므로」). 그래서 시작 전 작업이 있는 한 쥔 트레이를 내려놓을 수 없고,
 * 쥔 로봇은 초기화도 안 받는다(`INITIALIZE_ROBOT_HOLDING_TRAY`). 둘이 서로 막는다.
 *
 * | 차례 | 무엇 | 창구 |
 * | --- | --- | --- |
 * | 1 | 재료 · 레시피 버전 확인 — 다시 만들 수 없으면 **아무것도 하지 않는다** | `GET /batches/{id}` · `GET /recipes/{id}` |
 * | 2 | 이 작업 중단 | `POST /batches/{id}/stop` |
 * | 3 | 내려놓기가 켜질 때까지(상한) → 서버 순서의 첫 켜진 자리에 내려놓기 → 잡 `done` 까지(상한) | `GET /transfers/manual/options` · `POST /transfers/manual {place_held}` · `GET /transfers/manual/{id}` |
 * | 4 | 6축 실행이 빠지고 `IDLE` 이 될 때까지(상한) | `GET /modules` |
 * | 5 | 같은 레시피 · 시료 · 웰로 다시 만들고 새 작업으로 점검 | `recreateAndCheck` |
 *
 * 🔴 **작업을 없애는 것은 사람이 이 단추를 눌렀을 때뿐이다**(eric 2026-10-24 「자리 정리가 말 그대로 이슈만 정리해야지, 작업은 유지하고」).
 *    단추 이름과 확인 창이 «이 작업을 중단하고 같은 내용으로 다시 만든다»를 말한다. 다른 조치 단추는 작업을 끊지 않는다.
 * 🔴 **끊은 뒤에는 반드시 다시 만든다** — 내려놓기가 실패해도 5단계로 간다. 작업이 사라진 채로 끝나면 사람이 시료를 다시 담아야 한다.
 *    그때 내려놓기 실패 사유는 결과에 같이 싣는다.
 * 🔴 **레시피 버전** — `POST /batches` 는 버전을 받지 않고 서버가 «지금» committed 버전으로 만든다. 끊기 «전»에 맞대고, 만든 «뒤»에도 맞댄다.
 * 🔴 «됐다»는 다시 읽은 서버 값이 말한다(DP-04). 다시 읽기는 단계 안의 상한 대기에서만 한다(주기 폴링 아님).
 */

const JOB_RUNNING = 'running'
const JOB_DONE = 'done'
const IDLE = 'IDLE'

export interface HeldTrayPorts extends Pick<
  RecreatePorts,
  'createBatch' | 'choose' | 'preflight' | 'releaseHandoff'
> {
  readBatch: (batchId: string) => Promise<Batch>
  /** `GET /recipes/{id}` — 버전 · 상태만 본다 */
  readRecipe: (recipeId: string) => Promise<{ version: number; status: string }>
  stopBatch: (batchId: string, reason: string) => Promise<void>
  readPlaceOptions: () => Promise<ManualTransferOptions>
  placeHeld: (destPointId: string, reason: string) => Promise<ManualTransferJob>
  readManualTransfer: (jobId: string) => Promise<ManualTransferJob>
  listModules: () => Promise<readonly ModuleSnapshot[]>
  sleep: (ms: number) => Promise<void>
  now: () => number
  failure: (error: unknown) => string
}

export type HeldTrayResult =
  /** 다시 만들었다 — `placeFailed` 가 있으면 내려놓기는 못 했다(사유) */
  | { ok: true; batchId: string; result: PreflightResult; placeFailed?: string | undefined }
  /**
   * 멈췄다 — `stopped` 이면 이 작업은 이미 중단됐다(다시 만들기가 거절됐다) · `batchId` 는 새로 선 작업(버전이 달라 멈춘 때)
   */
  | { ok: false; reason: string; stopped: boolean; batchId?: string | undefined }

/** 그 모듈이 «쥔 채 멈춘 6축»인가 — 단추를 세울지(서버 판정은 서버가 한다 · 이것은 노출 조건) */
export function holdsTrayAtHandoff(module: ModuleSnapshot | undefined): boolean {
  if (module?.module_type !== 'collaborative_6axis' || module.state !== 'HANDOFF') return false
  const grips = Array.isArray(module.grips) ? module.grips : []
  return grips.some((grip) => grip.state === 'holding')
}

export async function runHeldTrayRecreate(
  ports: HeldTrayPorts,
  onStatus: (text: string) => void,
  input: { batchId: string; robotModuleId: string; reason: string },
): Promise<HeldTrayResult> {
  /* 1 — 재료 · 버전(끊기 «전») */
  let source: RecreateSource
  try {
    const got = recreateSourceOf(await ports.readBatch(input.batchId))
    if ('refused' in got) return { ok: false, reason: got.refused, stopped: false }
    source = got
    const refused = recipeVersionRefusal(source, await ports.readRecipe(source.recipeId))
    if (refused !== undefined) return { ok: false, reason: refused, stopped: false }
  } catch (error) {
    return { ok: false, reason: ports.failure(error), stopped: false }
  }

  /* 2 — 이 작업 중단 */
  onStatus(t('pfHeld.status.stopping', { batch: input.batchId.slice(0, 8) }))
  try {
    await ports.stopBatch(input.batchId, input.reason)
  } catch (error) {
    return { ok: false, reason: ports.failure(error), stopped: false }
  }

  /* 3 · 4 — 내려놓기. 실패해도 5로 간다(작업을 잃지 않는다) */
  const placeFailed = await placeHeld(ports, onStatus, input)

  /* 5 — 다시 만들기 · 새 작업 점검 */
  onStatus(t('pfAll.recreate.running'))
  try {
    const next = await recreateAndCheck(ports, source)
    return { ok: true, ...next, ...(placeFailed === undefined ? {} : { placeFailed }) }
  } catch (error) {
    if (error instanceof RecreateVersionMismatch)
      return { ok: false, reason: error.message, stopped: true, batchId: error.batchId }
    return { ok: false, reason: ports.failure(error), stopped: true }
  }
}

/** 내려놓기 — 실패면 사유, 됐으면 `undefined` */
async function placeHeld(
  ports: HeldTrayPorts,
  onStatus: (text: string) => void,
  input: { batchId: string; robotModuleId: string; reason: string },
): Promise<string | undefined> {
  try {
    /* 중단이 종착으로 내려앉기를 — 서버가 종착 안 한 배치를 세는 동안 내려놓기는 꺼져 있다 */
    const asked = ports.now()
    let choice = (await ports.readPlaceOptions()).place_held
    while (!choice.enabled && ports.now() - asked < QUICK_RELEASE_SETTLE_MS) {
      await ports.sleep(QUICK_IDLE_POLL_MS)
      choice = (await ports.readPlaceOptions()).place_held
    }
    if (!choice.enabled)
      return t('pfHeld.placeRefused', { reason: choice.reason ?? choice.code ?? '—' })
    const dest = choice.targets.find((target) => target.enabled)
    if (dest === undefined) return t('pfHeld.noTarget')
    const point = dest.dest.point_id
    onStatus(t('pfHeld.status.placing', { point }))
    let job = await ports.placeHeld(point, input.reason)
    const since = ports.now()
    while (job.status === JOB_RUNNING) {
      if (ports.now() - since >= QUICK_PLACE_TIMEOUT_MS)
        return t('pfHeld.placeTimeout', {
          seconds: String(Math.round(QUICK_PLACE_TIMEOUT_MS / 1000)),
          said: job.said,
        })
      await ports.sleep(QUICK_IDLE_POLL_MS)
      job = await ports.readManualTransfer(job.job_id)
    }
    if (job.status !== JOB_DONE)
      return t('pfHeld.placeHalted', { status: job.status, said: job.said })
    /* `done` 은 «놓았다»까지 — 6축은 대기 자리로 한 걸음 더 간다. 빠질 때까지 기다린다 */
    onStatus(t('pfHeld.status.settling'))
    const settle = ports.now()
    for (;;) {
      const robot = (await ports.listModules()).find(
        (module) => module.module_id === input.robotModuleId,
      )
      const executing =
        typeof robot?.current_execution?.execution_id === 'string' &&
        robot.current_execution.execution_id !== ''
      if (robot !== undefined && !executing && robot.state === IDLE) return undefined
      if (ports.now() - settle >= QUICK_IDLE_TIMEOUT_MS)
        return t('pfHeld.robotNotIdle', { state: robot?.state ?? '—' })
      await ports.sleep(QUICK_IDLE_POLL_MS)
    }
  } catch (error) {
    return ports.failure(error)
  }
}
